import { inject, Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';

import { CanvasElementType } from '@blaze-runtime/engine-domain';

import { BaseCommandHandler } from './base.command-handler';
import { AICommand, CommandType, ElementCommand } from './base.commands';
import { AICommandPayload } from './command-payload.model';

@Injectable({ providedIn: 'root' })
export class CommandPayloadFactory {
  private static readonly customCommandHandlers: Record<string, new () => BaseCommandHandler<any>> =
    {};
  private readonly injector = inject(Injector);
  private readonly commandHandler = inject(BaseCommandHandler);

  createCommandPayload(command: AICommand<any>): Observable<AICommandPayload<any>> {
    const handler =
      CommandPayloadFactory.customCommandHandlers[CommandPayloadFactory.getHashCode(command)];
    if (handler) {
      return this.injector.get(handler).handle(command);
    }

    return this.commandHandler.handle(command);
  }

  static registerCommandHandler(
    type: CommandType,
    elementType: CanvasElementType,
    handlerClass: new () => BaseCommandHandler<any>,
  ): void {
    CommandPayloadFactory.customCommandHandlers[
      CommandPayloadFactory.getElementCommandHashCode(type, elementType)
    ] = handlerClass;
  }

  static getHashCode(command: AICommand<any>): string {
    if (CommandPayloadFactory.isElementCommand(command)) {
      return CommandPayloadFactory.getElementCommandHashCode(command.type, command.elementType);
    }

    return command.type;
  }

  static getElementCommandHashCode(
    type: CommandType,
    elementType: CanvasElementType | '*',
  ): string {
    if (type === 'delete-element' || type === 'move-element' || elementType === '*') {
      // For delete element command, we don't need to specify the element type
      // because it can be used for any element type
      return type as string;
    }
    return `${type}:${elementType}`;
  }

  private static isElementCommand(
    command: Partial<AICommand<any>>,
  ): command is ElementCommand<any, any> {
    return 'elementType' in command && Boolean(command.elementType);
  }
}
